import React, { useState } from 'react';
import {useHistory, useParams} from "react-router-dom";
import moment from "moment";
import {useDispatch, useSelector} from "react-redux";

import MainToolbar from "../MainToolbar";
import {formatDate} from '../common/formatter';
import {useEffectAsync} from "../reactHelper";
import { t } from "../common/LocalizationProvider";
import {devicesSelectedActions} from "../store";
import SettingsDrawer from "../shared/SettingsDrawer";
import ConfirmHandler from "../shared/ConfirmHandler";

import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, IconButton, Typography, Switch, Button, Box, makeStyles, MenuItem, Menu
} from '@material-ui/core';
import { Edit, Delete, Link as LinkIcon, Email } from '@material-ui/icons';
import Snackbar from "@material-ui/core/Snackbar";
import MuiAlert from "@material-ui/lab/Alert";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import {useTheme} from "@material-ui/core/styles";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import Tooltip from "@material-ui/core/Tooltip";
import CssBaseline from "@material-ui/core/CssBaseline";
import {cyberStyles} from "../cssShared/cyberTheme";


const useStyles = makeStyles(theme => ({
    ...cyberStyles(theme),
}));

const DevicesInfo = () => {

    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const fixedBarHeight = isSmallScreen ? (3 * 48 + 3 * theme.spacing(1) + theme.spacing(4)) : 0;
    const classes = useStyles({fixedBarHeight});

    const history = useHistory();
    const { id } = useParams();
    const dispatch = useDispatch();

    const [devices, setDevices] = useState([]);
    const [removeDialogShown, setRemoveDialogShown] = useState(false);
    const [device, setDevice] = useState();
    const [openSnackbar, setOpenSnackbar] = useState(null);
    const [openActions, setOpenActions] = useState(null)
    const [hideDrawer, setHideDrawer] = useState(() => localStorage.getItem('hideDrawer') === 'true');

    const devicesSelected = useSelector(state => state.devicesSelected.devicesSelected ?? []);

    useEffectAsync(async () => {
        if (devicesSelected.length === 1 && devicesSelected[0] === 0) {
            dispatch(devicesSelectedActions.updateDevicesSelected([]));
            return;
        }
        const query = new URLSearchParams({ 'userId':id })
        devicesSelected.forEach(deviceId => query.append("id", deviceId))

        const response = await fetch(`/api/devices?${query.toString()}`);
        if (response.ok) {
            const res = await response.json()
            setDevices(res);
        }
    }, []);

    const textClass = (device) => {
        const expiration = moment(device.expiredAt);
        return  moment().isAfter(expiration) ? classes.redText : '';
    }

    const handleConnections = (device) => {
        history.push(`/admin/device/connections/${device.id}`);
        setOpenActions(null);
    }

    const handleEdit = (device) => {
        history.push(`/device/${device.id}`);
        setOpenActions(null);
    }

    const handleRemove = (device) => {
        setDevice(device)
        setRemoveDialogShown(true);
        setOpenActions(null);
    }

    const handleRemoveResult = (success) => {
        setRemoveDialogShown(false);
        if (success)
            setDevices(currentDevice => currentDevice.filter(d => d.id !== device.id))
    }

    const handleExport = async ( mail ) => {
        const headers = { Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' };
        const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const query = new URLSearchParams();
        devices.forEach(device => query.append("deviceIds", device.id))
        query.append("mail", mail)
        query.append("timeZone", timeZone)

        const response = await fetch(`/api/reports/devicesInfo?${query.toString()}`, { headers });
        if (response.ok){
            if (mail)
                setOpenSnackbar('EmailReportAlert')
            else {
                window.location.assign(window.URL.createObjectURL(await response.blob()));
                setOpenSnackbar('ExcelFileExported')
            }
        }
        else
            setOpenSnackbar('UnknownProblem')
    }

    const handleClose = (event, reason) => {
        if (reason === 'clickaway')
            return;
        setOpenSnackbar(null)
    }

    function Alert(props) {
        return <MuiAlert elevation={6} variant="filled" {...props} />;
    }

    const handleOpenActions = (event, device) => {
        setOpenActions(event.currentTarget)
        setDevice(device)
    }

    const handleGetAllDevices = async (event) => {
        const checked = event.target.checked;
        const query = new URLSearchParams({ 'userId':id })
        if (!checked)
            devicesSelected.forEach(deviceId => query.append("id", deviceId))
        const response = await fetch(`/api/devices?${query.toString()}`);
        if (response.ok) {
            const res = await response.json()
            setDevices(res);
        }
    }

    return (
        <div className={classes.settingRoot}>
            <CssBaseline />

            {!isSmallScreen && <div className={classes.appBarWrapper}>
                <MainToolbar/>
            </div>}

            <SettingsDrawer
                hideDrawer={hideDrawer}
                setHideDrawer={setHideDrawer}
                page={'devicesInfo'}
            />

            <main className={classes.content}>
                <div className={classes.toolbarSpacer} />

                <TableContainer component={Paper} className={classes.tableContainer}>
                    <Table size={isSmallScreen ? "small" : "medium"}>
                        <TableHead>
                            <TableRow>
                                <TableCell><strong>{t('sharedName')}</strong></TableCell>
                                <TableCell><strong>{t('deviceIdentifier')}</strong></TableCell>
                                <TableCell><strong>{t('sharedPhone')}</strong></TableCell>
                                <TableCell><strong>{t('creation')}</strong></TableCell>
                                <TableCell><strong>{t('userExpirationTime')}</strong></TableCell>
                                <TableCell align="center"><strong>{t('actions')}</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {devices.map((device, index) => (
                                <TableRow key={device.id}>
                                    <TableCell className={textClass(device)}>{device.name}</TableCell>
                                    <TableCell className={textClass(device)}>{device.uniqueId}</TableCell>
                                    <TableCell className={textClass(device)}>{device.phone}</TableCell>
                                    <TableCell className={textClass(device)}>{formatDate(device.createdAt)}</TableCell>
                                    <TableCell className={textClass(device)}>{formatDate(device.expiredAt)}</TableCell>
                                    {!isSmallScreen && (
                                        <TableCell align="right">
                                            <Tooltip title={t("sharedConnections")}>
                                                <IconButton onClick={() => handleConnections(device)}><LinkIcon/></IconButton>
                                            </Tooltip>
                                            <Tooltip title={t("sharedEdit")}>
                                                <IconButton onClick={() => handleEdit(device)}><Edit/></IconButton>
                                            </Tooltip>
                                            <Tooltip title={t("sharedRemove")}>
                                                <IconButton onClick={() => handleRemove(device)}><Delete/></IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    )}
                                    {isSmallScreen && (
                                        <TableCell align="right">
                                            <IconButton onClick={(event) => handleOpenActions(event, device)}>
                                                <MoreVertIcon style={{ fontSize: 20, color: '#00f2ff' }}/>
                                            </IconButton>
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Menu
                    open={!!openActions} anchorEl={openActions}
                    onClose={() => setOpenActions(null)}
                    anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                    transformOrigin={{vertical: 'bottom', horizontal: 'right'}}
                    getContentAnchorEl={null}
                    PaperProps={{ className: classes.menuPaper }}
                >
                    <MenuItem onClick={() => handleConnections(device)}>
                        <LinkIcon fontSize="small" style={{ marginRight: theme.spacing(1), color: '#00f2ff' }}/>{t('sharedConnections')}
                    </MenuItem>
                    <MenuItem onClick={() => handleEdit(device)}>
                        <Edit fontSize="small" style={{ marginRight: theme.spacing(1), color: '#00f2ff' }}/>{t('sharedEdit')}
                    </MenuItem>
                    <MenuItem onClick={() => handleRemove(device)}>
                        <Delete fontSize="small" style={{ marginRight: theme.spacing(1), color: '#ff4d4d' }}/>{t('sharedRemove')}
                    </MenuItem>
                </Menu>

                {!isSmallScreen && (
                    <Box
                        className={classes.actionPanel}
                        display="flex"
                        flexDirection= "row"
                        justifyContent="space-between"
                        alignItems="center"
                        p={2}>

                        <Box display="flex">
                            <Button
                                className={classes.cyberButton}
                                onClick={() => handleExport(false)}
                                variant="outlined"
                                style={{marginRight: 8}}
                            >
                                {t('reportExport')}
                            </Button>
                            <Button
                                className={classes.cyberButton}
                                onClick={() => handleExport(true)} startIcon={<Email/>}
                                variant="outlined"
                            >
                                {t('reportEmail')}
                            </Button>
                        </Box>
                        <Box display="flex" alignItems="center" justifyContent="flex-start" >
                            <Typography style={{color: 'rgba(0, 242, 255, 0.7)', marginRight: 8}} variant="body2">{t('allDevices')}</Typography>
                            <Switch color="primary" onClick={handleGetAllDevices}/>
                        </Box>
                    </Box>)}

                {isSmallScreen && (
                    <Box
                        display="flex"
                        flexDirection= "column"
                        alignItems="stretch"
                        className={classes.fixedBottomBar}
                    >
                        <Button
                            fullWidth
                            className={classes.cyberButton}
                            variant="outlined"
                            onClick={() => handleExport(false)}
                        >
                            <strong>{t('reportExport')}</strong>
                        </Button>
                        <Button
                            fullWidth
                            className={classes.cyberButton}
                            variant="outlined"
                            onClick={() => handleExport(true)} startIcon={<Email/>}
                        >
                            <strong>{t('reportEmail')}</strong>
                        </Button>
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Typography variant="body2" style={{color: 'rgba(255,255,255,0.7)'}}>{t('allDevices')}</Typography>
                            <Switch color="primary" onClick={handleGetAllDevices}/>
                        </Box>
                    </Box>)}
            </main>
            {device?.id && <ConfirmHandler open={removeDialogShown} endpoint="devices" itemId={device.id} handleCloseDialog={handleRemoveResult} />}
            <Snackbar open={!!openSnackbar} autoHideDuration={6000} onClose={handleClose}>
                <Alert onClose={handleClose} severity="success">
                    {t(openSnackbar)}
                </Alert>
            </Snackbar>
        </div>
    );
};


export default DevicesInfo;